import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Chip,
    Tooltip,
    Modal,
    CircularProgress,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Print as PrintIcon,
    Payment as PaymentIcon,
    FilterList as FilterIcon,
    FilterAltOff as ClearFilterIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { invoiceService } from '../../../services/invoiceService';
import InvoiceForm from './InvoiceForm';
import { toLocalYmd } from '../../../utils/formatters';

const parseYmd = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const isLastDayOfMonth = (date) => {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    return next.getMonth() !== date.getMonth();
};

/**
 * The default month range used to be built with toISOString(), which in IST
 * saved e.g. September as 2026-08-31 to 2026-09-29 and so hid invoices dated on
 * the last day of the month. That range was persisted, so recognise its exact
 * shape (last day of one month to the day before the next month's last day)
 * and move it forward a day.
 */
const repairShiftedMonthRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return { fromDate, toDate };
    const from = parseYmd(fromDate);
    const to = parseYmd(toDate);
    const fixedFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
    const fixedTo = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1);
    const looksShifted = isLastDayOfMonth(from)
        && isLastDayOfMonth(fixedTo)
        && fixedFrom.getMonth() === fixedTo.getMonth()
        && fixedFrom.getFullYear() === fixedTo.getFullYear();
    return looksShifted
        ? { fromDate: toLocalYmd(fixedFrom), toDate: toLocalYmd(fixedTo) }
        : { fromDate, toDate };
};

const InvoiceList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);

    // localStorage key for invoice filters
    const INVOICE_FILTERS_KEY = 'invoice_filters_persistence';

    // Set default date range to current month
    const getCurrentMonthRange = () => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            fromDate: toLocalYmd(startOfMonth),
            toDate: toLocalYmd(endOfMonth)
        };
    };

    // Load saved filters from localStorage
    const loadSavedFilters = () => {
        try {
            const saved = localStorage.getItem(INVOICE_FILTERS_KEY);
            if (saved) {
                const filters = JSON.parse(saved);
                const range = repairShiftedMonthRange(
                    filters.fromDate || getCurrentMonthRange().fromDate,
                    filters.toDate || getCurrentMonthRange().toDate
                );
                return {
                    searchText: filters.searchText || '',
                    statusFilter: filters.statusFilter || '',
                    invoiceTypeFilter: filters.invoiceTypeFilter || '',
                    fromDate: range.fromDate,
                    toDate: range.toDate,
                    selectedCustomer: filters.selectedCustomer || null,
                    fromDueDate: filters.fromDueDate || '',
                    toDueDate: filters.toDueDate || ''
                };
            }
        } catch (error) {
            console.error('Error loading saved invoice filters:', error);
        }
        return null;
    };

    // Save filters to localStorage
    const saveFilters = (filters) => {
        try {
            localStorage.setItem(INVOICE_FILTERS_KEY, JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving invoice filters:', error);
        }
    };

    // Load saved filters or use defaults
    const savedFilters = loadSavedFilters();
    const defaultRange = getCurrentMonthRange();

    const [searchText, setSearchText] = useState(savedFilters?.searchText || '');
    const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || '');
    const [invoiceTypeFilter, setInvoiceTypeFilter] = useState(savedFilters?.invoiceTypeFilter || '');
    const [fromDate, setFromDate] = useState(savedFilters?.fromDate || defaultRange.fromDate);
    const [toDate, setToDate] = useState(savedFilters?.toDate || defaultRange.toDate);
    const [selectedCustomer, setSelectedCustomer] = useState(savedFilters?.selectedCustomer || null);
    const [fromDueDate, setFromDueDate] = useState(savedFilters?.fromDueDate || '');
    const [toDueDate, setToDueDate] = useState(savedFilters?.toDueDate || '');
    const [customerInput, setCustomerInput] = useState('');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({ invoiceFrom: '', invoiceTo: '', dueFrom: '', dueTo: '' });

    // Status update dialog
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [invoiceToUpdate, setInvoiceToUpdate] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [paidDate, setPaidDate] = useState('');
    const [paymentMode, setPaymentMode] = useState('');
    const [paymentReferenceNo, setPaymentReferenceNo] = useState('');
    const [paymentErrors, setPaymentErrors] = useState({});

    // Save filters to localStorage whenever they change
    useEffect(() => {
        const currentFilters = {
            searchText,
            statusFilter,
            invoiceTypeFilter,
            fromDate,
            toDate,
            selectedCustomer,
            fromDueDate,
            toDueDate
        };
        saveFilters(currentFilters);
    }, [searchText, statusFilter, invoiceTypeFilter, fromDate, toDate, selectedCustomer, fromDueDate, toDueDate]);

    // Fetch on mount and whenever pagination changes
    useEffect(() => {
        fetchInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    // Do not auto-fetch on search text change; fetch on Apply Filters button

    // Debounced customer search
    useEffect(() => {
        let active = true;
        const run = async () => {
            const term = customerInput?.trim();
            if (!term || term.length < 2) {
                if (active) setCustomerOptions([]);
                return;
            }
            setCustomerLoading(true);
            try {
                const result = await invoiceService.searchCustomers(term);
                if (active && result.success) {
                    const opts = (result.data || []).map((c) => ({
                        id: c.id,
                        label: c.cust_name ? `${c.cust_name} (${c.cust_code})` : c.cust_code || 'Unknown',
                        raw: c
                    }));
                    setCustomerOptions(opts);
                }
            } catch (e) {
                // ignore
            } finally {
                if (active) setCustomerLoading(false);
            }
        };
        const t = setTimeout(run, 300);
        return () => {
            active = false;
            clearTimeout(t);
        };
    }, [customerInput]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                per_page: rowsPerPage,
                search: searchText,
                status: statusFilter
            };

            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (selectedCustomer?.id) params.customer_id = selectedCustomer.id;
            if (fromDueDate) params.from_due_date = fromDueDate;
            if (toDueDate) params.to_due_date = toDueDate;
            if (invoiceTypeFilter) params.invoice_type = invoiceTypeFilter;

            const response = await invoiceService.getAllInvoices(params);
            if (response.success) {
                setInvoices(response.data);
                setTotalCount(response.meta?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            showError('Failed to fetch invoices. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const validateFilters = () => {
        const errors = { invoiceFrom: '', invoiceTo: '', dueFrom: '', dueTo: '' };
        let valid = true;
        // Invoice date pair validation
        if ((fromDate && !toDate) || (!fromDate && toDate)) {
            errors.invoiceFrom = 'Select both From and To';
            errors.invoiceTo = 'Select both From and To';
            valid = false;
        }
        if (fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                errors.invoiceTo = 'To date must be after From date';
                valid = false;
            }
        }
        // Due date pair validation
        if ((fromDueDate && !toDueDate) || (!fromDueDate && toDueDate)) {
            errors.dueFrom = 'Select both From and To';
            errors.dueTo = 'Select both From and To';
            valid = false;
        }
        if (fromDueDate && toDueDate) {
            if (new Date(fromDueDate) > new Date(toDueDate)) {
                errors.dueTo = 'To date must be after From date';
                valid = false;
            }
        }
        setFieldErrors(errors);
        return valid;
    };

    // The stats strip only changes when the filters or the invoices change,
    // not when paging, so it is refreshed from those places alone (the parent
    // loads it once on mount).
    const refreshStats = () => {
        if (!onDataChange) return;
        onDataChange({
            from_date: fromDate,
            to_date: toDate,
            from_due_date: fromDueDate,
            to_due_date: toDueDate,
            customer_id: selectedCustomer?.id,
            status: statusFilter,
            invoice_type: invoiceTypeFilter || undefined,
            search: searchText
        });
    };

    const handleApplyFilters = () => {
        if (!validateFilters()) return;
        setPage(0);
        fetchInvoices();
        // also refresh stats with same filters if parent provided onDataChange
        if (onDataChange) {
            const params = {};
            if (fromDate && toDate) { params.from_date = fromDate; params.to_date = toDate; }
            if (fromDueDate && toDueDate) { params.from_due_date = fromDueDate; params.to_due_date = toDueDate; }
            if (selectedCustomer?.id) params.customer_id = selectedCustomer.id;
            if (statusFilter) params.status = statusFilter;
            if (invoiceTypeFilter) params.invoice_type = invoiceTypeFilter;
            if (searchText) params.search = searchText;
            onDataChange(params);
        }
    };

    const handleResetFilters = () => {
        setFromDate('');
        setToDate('');
        setFromDueDate('');
        setToDueDate('');
        setSelectedCustomer(null);
        setCustomerInput('');
        setStatusFilter('');
        setInvoiceTypeFilter('');
        setSearchText('');
        setFieldErrors({ invoiceFrom: '', invoiceTo: '', dueFrom: '', dueTo: '' });
        setPage(0);

        // Clear saved filters from localStorage
        try {
            localStorage.removeItem(INVOICE_FILTERS_KEY);
        } catch (error) {
            console.error('Error clearing saved invoice filters:', error);
        }

        // Fetch with cleared filters
        setTimeout(() => {
            fetchInvoices();
            if (onDataChange) onDataChange({});
        }, 0);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPage(0);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(0);
    };

    const handleAdd = () => {
        setSelectedInvoice(null);
        setIsModalVisible(true);
    };

    const handleView = (invoice) => {
        // Navigate to the standalone invoice details page
        navigate(`/employee/invoices/${invoice.invoice_id}`);
    };

    const handleUpdateStatus = (invoice) => {
        setInvoiceToUpdate(invoice);
        setNewStatus(invoice.status);
        
        // If invoice is already paid, populate payment fields with existing data
        if (invoice.status === 'paid') {
            // Format paid_date to YYYY-MM-DD for date input (handle both ISO and date-only formats)
            let formattedPaidDate = '';
            if (invoice.paid_date) {
                const date = new Date(invoice.paid_date);
                if (!isNaN(date.getTime())) {
                    formattedPaidDate = date.getFullYear() + '-' + 
                        String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(date.getDate()).padStart(2, '0');
                }
            }
            
            setPaidDate(formattedPaidDate);
            setPaymentMode(invoice.payment_mode || '');
            setPaymentReferenceNo(invoice.payment_reference_no || '');
        } else {
            // Reset payment fields for non-paid invoices
            setPaidDate('');
            setPaymentMode('');
            setPaymentReferenceNo('');
        }
        
        setPaymentErrors({});
        setStatusDialogOpen(true);
    };

    const handleStatusUpdateConfirm = async () => {
        if (invoiceToUpdate && newStatus) {
            // Validate payment fields if status is 'paid'
            if (newStatus === 'paid') {
                const errors = {};
                if (!paidDate) {
                    errors.paidDate = 'Payment date is required';
                }
                if (!paymentMode) {
                    errors.paymentMode = 'Payment mode is required';
                }
                if (!paymentReferenceNo || paymentReferenceNo.trim() === '') {
                    errors.paymentReferenceNo = 'Payment reference number is required';
                }

                if (Object.keys(errors).length > 0) {
                    setPaymentErrors(errors);
                    return;
                }
            }

            try {
                const statusData = { status: newStatus };
                
                // Add payment details if status is 'paid'
                if (newStatus === 'paid') {
                    statusData.paid_date = paidDate;
                    statusData.payment_mode = paymentMode;
                    statusData.payment_reference_no = paymentReferenceNo;
                }

                const response = await invoiceService.updateInvoiceStatus(
                    invoiceToUpdate.invoice_id,
                    statusData
                );
                if (response.success) {
                    showSuccess(response.message || 'Invoice status updated successfully');
                    fetchInvoices();
                    refreshStats();
                } else {
                    showError(response.message || 'Failed to update invoice status');
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    // Handle validation errors from backend
                    const backendErrors = error.response.data.errors;
                    const formattedErrors = {};
                    if (backendErrors.paid_date) formattedErrors.paidDate = backendErrors.paid_date[0];
                    if (backendErrors.payment_mode) formattedErrors.paymentMode = backendErrors.payment_mode[0];
                    if (backendErrors.payment_reference_no) formattedErrors.paymentReferenceNo = backendErrors.payment_reference_no[0];
                    
                    if (Object.keys(formattedErrors).length > 0) {
                        setPaymentErrors(formattedErrors);
                        return;
                    }
                }
                
                if (error.response?.data?.message) {
                    showError(error.response.data.message);
                } else {
                    showError('Failed to update invoice status. Please try again.');
                }
            }
        }
        setStatusDialogOpen(false);
        setInvoiceToUpdate(null);
        setNewStatus('');
        setPaidDate('');
        setPaymentMode('');
        setPaymentReferenceNo('');
        setPaymentErrors({});
    };

    const handlePrint = (invoice) => {
        // Navigate to invoice details page where user can use the print button
        navigate(`/employee/invoices/${invoice.invoice_id}`);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedInvoice(null);
    };

    

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setSelectedInvoice(null);
        fetchInvoices();
        refreshStats();
        showSuccess('Invoice generated successfully');
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        return invoiceService.getStatusColor(status);
    };

    const formatDate = (dateString) => {
        return invoiceService.formatDate(dateString);
    };

    const formatCurrency = (amount) => {
        return invoiceService.formatCurrency(amount);
    };

    const isOverdue = (dueDate, status) => {
        return invoiceService.isOverdue(dueDate, status);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Invoice List</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Generate Invoice
                </Button>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldErrors.invoiceFrom}
                        helperText={fieldErrors.invoiceFrom}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => { setToDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldErrors.invoiceTo}
                        helperText={fieldErrors.invoiceTo}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="From Due Date"
                        type="date"
                        value={fromDueDate}
                        onChange={(e) => { setFromDueDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldErrors.dueFrom}
                        helperText={fieldErrors.dueFrom}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="To Due Date"
                        type="date"
                        value={toDueDate}
                        onChange={(e) => { setToDueDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldErrors.dueTo}
                        helperText={fieldErrors.dueTo}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete
                        options={customerOptions}
                        loading={customerLoading}
                        value={selectedCustomer}
                        onChange={(event, newValue) => { setSelectedCustomer(newValue); setPage(0); }}
                        inputValue={customerInput}
                        onInputChange={(event, newInputValue) => setCustomerInput(newInputValue)}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Customer"
                                placeholder="Search customer"
                                fullWidth
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                        <InputLabel>Status Filter</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status Filter"
                            onChange={(e) => handleStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                            <MenuItem value="overdue">Overdue</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                        <InputLabel>Customer Type</InputLabel>
                        <Select
                            value={invoiceTypeFilter}
                            label="Customer Type"
                            onChange={(e) => { setInvoiceTypeFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="">All Customers</MenuItem>
                            <MenuItem value="credit">Credit</MenuItem>
                            <MenuItem value="to_pay">To-Pay</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth
                        placeholder="Search by invoice number, customer, GSTIN..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="stretch">
                        <Button
                            variant="contained"
                            startIcon={<FilterIcon />}
                            onClick={handleApplyFilters}
                            fullWidth
                            sx={{ height: (theme) => theme.spacing(7) }}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ClearFilterIcon />}
                            onClick={handleResetFilters}
                            sx={{ height: (theme) => theme.spacing(7), px: 2.5 }}
                        >
                            Reset
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Invoice No.</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Invoice Date</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Shipments</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : invoices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No invoices found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            invoices.map((invoice) => (
                                <TableRow key={invoice.invoice_id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice.invoice_no}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {invoice.invoice_type === 'to_pay' ? (
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {invoice.billing_name || 'N/A'}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Chip label="To-Pay" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {invoice.billing_gst_no}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {invoice.customer?.cust_name ? `${invoice.customer.cust_name} (${invoice.customer.cust_code})` : `${invoice.customer?.from_name || 'N/A'} (${invoice.customer?.cust_code || 'N/A'})`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {invoice.customer?.from_name && invoice.customer?.from_name !== invoice.customer?.from_company ? invoice.customer.from_name : 'N/A'}
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(invoice.invoice_date)}
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {formatDate(invoice.due_date)}
                                            </Typography>
                                            {isOverdue(invoice.due_date, invoice.status) && (
                                                <Chip
                                                    label="Overdue"
                                                    color="error"
                                                    size="small"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {formatCurrency(invoice.grand_total)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={invoice.total_shipments}
                                            color="info"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={invoiceService.getStatusLabel(invoice.status)}
                                            color={getStatusColor(invoice.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {/* View action removed: use Print/Open to navigate to details page */}
                                            <Tooltip title="Update Status">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleUpdateStatus(invoice)}
                                                    disabled={invoice.status === 'cancelled'}
                                                >
                                                    <PaymentIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={invoice.status === 'cancelled' ? 'View Invoice (printing disabled)' : 'Open & Print Invoice'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handlePrint(invoice)}
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Invoice Generation Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="invoice-form-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <InvoiceForm
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                    />
                </Box>
            </Modal>

            

            {/* Status Update Dialog */}
            <Dialog 
                open={statusDialogOpen} 
                onClose={() => setStatusDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Invoice Status</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newStatus}
                                label="Status"
                                onChange={(e) => {
                                    setNewStatus(e.target.value);
                                    setPaymentErrors({});
                                }}
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="overdue">Overdue</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Payment Details - Show only when status is 'paid' */}
                        {newStatus === 'paid' && (
                            <>
                                <TextField
                                    fullWidth
                                    label="Payment Date"
                                    type="date"
                                    value={paidDate}
                                    onChange={(e) => {
                                        setPaidDate(e.target.value);
                                        setPaymentErrors({...paymentErrors, paidDate: ''});
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    error={!!paymentErrors.paidDate}
                                    helperText={paymentErrors.paidDate}
                                    sx={{ mb: 2 }}
                                />

                                <FormControl 
                                    fullWidth 
                                    sx={{ mb: 2 }}
                                    error={!!paymentErrors.paymentMode}
                                >
                                    <InputLabel>Payment Mode</InputLabel>
                                    <Select
                                        value={paymentMode}
                                        label="Payment Mode"
                                        onChange={(e) => {
                                            setPaymentMode(e.target.value);
                                            setPaymentErrors({...paymentErrors, paymentMode: ''});
                                        }}
                                    >
                                        <MenuItem value="UPI">UPI</MenuItem>
                                        <MenuItem value="NEFT">NEFT</MenuItem>
                                        <MenuItem value="RTGS">RTGS</MenuItem>
                                        <MenuItem value="CASH">Cash</MenuItem>
                                        <MenuItem value="CHEQUE">Cheque</MenuItem>
                                        <MenuItem value="OTHER">Other</MenuItem>
                                    </Select>
                                    {paymentErrors.paymentMode && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                            {paymentErrors.paymentMode}
                                        </Typography>
                                    )}
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Payment Reference No / URN / Transaction No"
                                    value={paymentReferenceNo}
                                    onChange={(e) => {
                                        setPaymentReferenceNo(e.target.value);
                                        setPaymentErrors({...paymentErrors, paymentReferenceNo: ''});
                                    }}
                                    error={!!paymentErrors.paymentReferenceNo}
                                    helperText={paymentErrors.paymentReferenceNo}
                                    placeholder="Enter reference number, URN, or transaction ID"
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleStatusUpdateConfirm}
                        variant="contained"
                        disabled={!newStatus}
                    >
                        Update Status
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InvoiceList; 